import { useCallback, useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import type { SimulationRendererProps, RosterOrganism, BodyTypeSchema, GeomDef, MeshGeomDef, MeshData } from '../types/simulation';

const MAX_FIXTURES = 4096;
const MAX_SITES    = 512;
const RECORD_BYTES = 43;  // 23 base + 16 quaternion + 4 geom_scale
const MAX_PARTICLES         = 16384;  // managed-field cells (plants); matches substrate MAX_PARTICLES
const PARTICLE_RECORD_BYTES = 25;     // slot(2)+owner(4)+xyz(12)+radius(4)+rgb(3)
const PARTICLE_SUBHEADER_BYTES = 7;   // flags(1)+removed_count(2)+state_hash(4)
const PARTICLE_RK_COOLDOWN  = 30;     // min frames between keyframe re-requests (retries a lost keyframe)
const MAX_FROZEN            = 262144; // frozen (LOD unit) cells cap; matches ecology MAX_FROZEN
const FROZEN_SUBHEADER_BYTES = 13;    // flags(1)+added_count(4)+removed_count(4)+cid_set_hash(4)
const FROZEN_RECORD_BYTES    = 27;    // cid(4)+owner(4)+xyz(12)+radius(4)+rgb(3)
const FROZEN_RK_COOLDOWN     = 30;    // min frames between frozen keyframe re-requests

// CRC32 (IEEE, reflected) — byte-identical to Python's zlib.crc32, so the client can
// verify its particle state against the server's embedded hash.
const _CRC32_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
    t[n] = c >>> 0;
  }
  return t;
})();
function crc32(bytes: Uint8Array): number {
  let c = 0xFFFFFFFF;
  for (let i = 0; i < bytes.length; i++) c = (_CRC32_TABLE[(c ^ bytes[i]) & 0xFF] ^ (c >>> 8)) >>> 0;
  return (c ^ 0xFFFFFFFF) >>> 0;
}
// crc32 of a cid as 4 little-endian bytes — matches Python zlib.crc32(struct.pack("<I", cid)),
// the unit the frozen cid-set hash is accumulated from on both ends.
const _cidBuf = new Uint8Array(4);
function crc32Cid(cid: number): number {
  _cidBuf[0] = cid & 0xFF; _cidBuf[1] = (cid >>> 8) & 0xFF;
  _cidBuf[2] = (cid >>> 16) & 0xFF; _cidBuf[3] = (cid >>> 24) & 0xFF;
  return crc32(_cidBuf);
}
const _dummy = new THREE.Object3D();
const _color = new THREE.Color();
const _skyColor = new THREE.Color();
new THREE.Vector3();
const _fwd   = new THREE.Vector3();

// Coordinate transform: sim(x,y,z) → Three.js(x,z,-y)  =  rotation -90° around X
// Used to convert both positions and quaternions from sim space to Three.js space.
const COORD_QUAT     = new THREE.Quaternion(-Math.SQRT1_2, 0, 0, Math.SQRT1_2); // R_x(-90°)
const COORD_QUAT_INV = new THREE.Quaternion( Math.SQRT1_2, 0, 0, Math.SQRT1_2); // R_x(+90°)

function simQuatToThree(qw: number, qx: number, qy: number, qz: number): THREE.Quaternion {
  const q = new THREE.Quaternion(qx, qy, qz, qw); // THREE uses (x,y,z,w)
  return COORD_QUAT.clone().multiply(q).multiply(COORD_QUAT_INV);
}

function b64ToFloat32(b64: string): Float32Array {
  const bin = atob(b64);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return new Float32Array(buf.buffer);
}

function b64ToInt32(b64: string): Int32Array {
  const bin = atob(b64);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return new Int32Array(buf.buffer);
}

// Build a Three.js BufferGeometry from a MuJoCo geom definition.
// Geometry is defined in the body's Three.js local frame (after coord transform).
// Axes after coord transform: local-x=sim-x, local-y=sim-z, local-z=-sim-y
function makeGeomGeometry(geom: GeomDef, meshes?: Record<string, MeshData>): THREE.BufferGeometry {
  switch (geom.type) {
    case 'sphere': {
      const [s0] = geom.size;
      return new THREE.SphereGeometry(s0, 12, 8);
    }
    case 'capsule': {
      // MuJoCo capsule: axis along body-local z → Three.js local y after coord transform
      // size[0]=radius, size[1]=half-length of cylindrical part
      const [s0, s1] = geom.size;
      return new THREE.CapsuleGeometry(s0, s1 * 2, 4, 12);
    }
    case 'cylinder': {
      // Same axis mapping as capsule
      const [s0, s1] = geom.size;
      return new THREE.CylinderGeometry(s0, s0, s1 * 2, 12);
    }
    case 'box': {
      // size[0,1,2] = half-extents along sim x,y,z
      // → Three.js local: x=s0, y=s2, z=s1 (after coord transform)
      const [s0, s1, s2] = geom.size;
      return new THREE.BoxGeometry(s0 * 2, s2 * 2, s1 * 2);
    }
    case 'ellipsoid':
      return new THREE.SphereGeometry(1, 12, 8);
    case 'mesh': {
      const g = geom as MeshGeomDef;
      const meshData = meshes?.[g.mesh_key];
      if (!meshData) return new THREE.SphereGeometry(0.05, 6, 4); // fallback if schema incomplete
      const verts = b64ToFloat32(meshData.verts_b64);
      const faces  = b64ToInt32(meshData.faces_b64);
      // Transform vertices from sim Z-up (x,y,z) → Three.js Y-up (x,z,-y)
      for (let i = 0; i < verts.length; i += 3) {
        const vy = verts[i + 1], vz = verts[i + 2];
        verts[i + 1] = vz;
        verts[i + 2] = -vy;
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(verts, 3));
      geo.setIndex(new THREE.BufferAttribute(new Uint32Array(faces.buffer), 1));
      geo.computeVertexNormals();
      return geo;
    }
    default:
      return new THREE.SphereGeometry(0.05, 6, 4);
  }
}

function makeGeomMesh(geom: GeomDef, meshes?: Record<string, MeshData>): THREE.Mesh {
  const geo = makeGeomGeometry(geom, meshes);
  const [, , , a] = geom.rgba;
  const mat = new THREE.MeshStandardMaterial({
    color: new THREE.Color(0.90, 0.48, 0.15),
    roughness: 0.65,
    metalness: 0.05,
    transparent: a < 0.99,
    opacity: a,
  });
  const mesh = new THREE.Mesh(geo, mat);

  // Local position: apply coord transform to geom_pos (sim body-local)
  const [px, py, pz] = geom.pos;
  mesh.position.set(px, pz, -py);

  // Local orientation: apply coord transform to geom_quat
  const [qw, qx, qy, qz] = geom.quat;
  mesh.quaternion.copy(simQuatToThree(qw, qx, qy, qz));

  // Ellipsoid: scale the unit sphere by semi-axes
  if (geom.type === 'ellipsoid') {
    const [s0, s1, s2] = geom.size;
    mesh.scale.set(s0, s2, s1); // sim-x→three-x, sim-z→three-y, sim-y→three-z
  }

  return mesh;
}

// Build one Three.js Group for a single body segment, containing all its geom meshes.
function buildSegmentGroup(segment: { name?: string; geoms: GeomDef[] }, meshes?: Record<string, MeshData>): THREE.Group {
  const group = new THREE.Group();
  for (const geom of segment.geoms) {
    if (geom.type === 'mesh') {
      const key = (geom as MeshGeomDef).mesh_key;
      const md = meshes?.[key];
      console.log(`[sim] mesh geom key=${key} found=${!!md} verts=${md ? b64ToFloat32(md.verts_b64).length / 3 : 0}`);
    }
    group.add(makeGeomMesh(geom, meshes));
  }
  return group;
}

export default function DefaultSimRenderer({
  broadcastUrl,
  config,
  selectedEntityId,
  hoveredEntityId,
  snapToEntityId,
  onOrganismClick,
  onOrganismHover,
}: SimulationRendererProps) {
  const [broadcastOffline, setBroadcastOffline] = useState(false);
  const [webglUnavailable, setWebglUnavailable] = useState(false);
  const [isLocked, setIsLocked] = useState(false);  // pointer-lock (FPS) state, for the crosshair/hint overlay

  const mountRef   = useRef<HTMLDivElement>(null);
  const tickDisplayRef = useRef<HTMLSpanElement>(null);
  const tickRateRef    = useRef<HTMLSpanElement>(null);
  const wsRef      = useRef<WebSocket | null>(null);
  const rosterRef  = useRef<RosterOrganism[]>([]);
  const siteRegistryRef = useRef<string[]>([]);
  const latestBufRef = useRef<{ buf: ArrayBuffer; view: DataView } | null>(null);
  const instanceIdToOrgIdxRef = useRef(new Map<number, number>());

  // Latest sun state from the world server (via broadcast 'sun' message).
  const sunRef = useRef<{
    direction: [number, number, number];
    rgb: [number, number, number];
    intensity: number;
    is_day: boolean;
  } | null>(null);

  // MJCF body schema state
  const bodySchemaRef    = useRef<Record<string, BodyTypeSchema>>({});
  // entity_id → array of Three.js Groups (one per fixture slot / body segment)
  const entityGroupsRef  = useRef<Map<string, THREE.Group[]>>(new Map());
  // fixture slot → { entityId, segIdx } for schema-rendered slots
  const slotToEntityRef  = useRef<Map<number, { entityId: string; segIdx: number }>>(new Map());
  // Reference to the scene for adding/removing groups (set during scene setup)
  const sceneRef         = useRef<THREE.Scene | null>(null);
  const fixturesRef      = useRef<THREE.InstancedMesh | null>(null); // sphere fallback
  const particlesRef     = useRef<THREE.InstancedMesh | null>(null); // managed-field cells (plants)
  const frozenRef        = useRef<THREE.InstancedMesh | null>(null); // frozen LOD-unit cells (static)
  // Persistent particle instance state, so sleeping cells cost nothing per frame:
  // the wire stream is keyframe + deltas, and cells not mentioned keep their instance.
  const particleSlotToIdxRef = useRef<Map<number, number>>(new Map()); // slot -> instance index
  const particleIdxToSlotRef = useRef<number[]>([]);                   // instance index -> slot
  const particleHasKeyframeRef = useRef(false);                       // ignore deltas until first keyframe
  const particleRawRef = useRef<Map<number, Uint8Array>>(new Map());  // slot -> raw 25-byte record (for hash)
  const particleHashRef = useRef(0);                                  // running XOR of crc32(record) over our cells
  const particleCrcFrameRef = useRef(0);                              // frames since connect, for request cadence
  const lastRkFrameRef = useRef(-PARTICLE_RK_COOLDOWN);               // last keyframe-request frame
  // Frozen (LOD unit) instance state: static geometry, append/keyframe stream.
  const frozenCidToIdxRef = useRef<Map<number, number>>(new Map());   // frozen cid -> instance index
  const frozenIdxToCidRef = useRef<number[]>([]);                     // instance index -> cid
  const frozenHasKeyframeRef = useRef(false);
  const frozenHashRef = useRef(0);                                    // running XOR of crc32(cid) over our frozen set
  const frozenCrcFrameRef = useRef(0);                               // frozen frames since connect, for request cadence
  const lastFrozenRkFrameRef = useRef(-FROZEN_RK_COOLDOWN);           // last frozen keyframe-request frame

  const selectedEntityIdRef = useRef(selectedEntityId);
  const hoveredEntityIdRef  = useRef(hoveredEntityId);
  const snapToEntityIdRef   = useRef(snapToEntityId);
  const onOrganismClickRef  = useRef(onOrganismClick);
  const onOrganismHoverRef  = useRef(onOrganismHover);
  const entityPositionsRef  = useRef<Map<string, THREE.Vector3>>(new Map());
  const raycasterTargetsRef = useRef<THREE.Mesh[]>([]);

  useEffect(() => { selectedEntityIdRef.current = selectedEntityId; }, [selectedEntityId]);
  useEffect(() => { hoveredEntityIdRef.current  = hoveredEntityId;  }, [hoveredEntityId]);
  useEffect(() => { snapToEntityIdRef.current   = snapToEntityId;   }, [snapToEntityId]);
  useEffect(() => { onOrganismClickRef.current  = onOrganismClick;  }, [onOrganismClick]);
  useEffect(() => { onOrganismHoverRef.current  = onOrganismHover;  }, [onOrganismHover]);

  // Rebuild entity groups whenever roster or schemas change.
  const rebuildEntityGroups = useCallback(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    const roster = rosterRef.current;
    const schemas = bodySchemaRef.current;
    const entityGroups = entityGroupsRef.current;
    const slotToEntity = slotToEntityRef.current;

    // Remove groups for entities no longer in roster (or dead)
    const rosterIds = new Set(roster.filter(r => r.state !== 'dead').map(r => r.entity_id));
    for (const [entityId, groups] of entityGroups) {
      if (!rosterIds.has(entityId)) {
        for (const g of groups) scene.remove(g);
        entityGroups.delete(entityId);
        // Clean up slot mapping
        for (const [slot, info] of slotToEntity) {
          if (info.entityId === entityId) slotToEntity.delete(slot);
        }
      }
    }

    // Create groups for new entities that have a schema
    for (const org of roster.filter(r => r.state !== 'dead')) {
      if (entityGroups.has(org.entity_id)) continue;
      const bodyType = org.body_type;
      if (!bodyType || !schemas[bodyType]) continue;

      const schema = schemas[bodyType];
      const groups: THREE.Group[] = [];
      for (let segIdx = 0; segIdx < schema.bodies.length && segIdx < org.fixture_slots.length; segIdx++) {
        const group = buildSegmentGroup(schema.bodies[segIdx], schema.meshes);
        group.traverse(obj => { if (obj instanceof THREE.Mesh) obj.userData.entityId = org.entity_id; });
        scene.add(group);
        groups.push(group);
        slotToEntity.set(org.fixture_slots[segIdx], { entityId: org.entity_id, segIdx });
      }
      if (groups.length > 0) entityGroups.set(org.entity_id, groups);
    }

    // Rebuild flat mesh list for raycasting
    const targets: THREE.Mesh[] = [];
    for (const groups of entityGroups.values()) {
      for (const g of groups) {
        g.traverse(obj => { if (obj instanceof THREE.Mesh) targets.push(obj); });
      }
    }
    raycasterTargetsRef.current = targets;
  }, []);

  useEffect(() => {
    const scene = sceneRef.current;
    const foodItems = config?.world?.static_objects?.food;
    if (!scene || !foodItems?.length) return;

    const geo = new THREE.SphereGeometry(0.4, 8, 6);
    const mat = new THREE.MeshStandardMaterial({
      color: 0x5aaa3a,
      emissive: new THREE.Color(0.2, 0.5, 0.05),
      emissiveIntensity: 1.2,
      roughness: 0.7,
      metalness: 0.0,
    });
    const meshes = foodItems.map(item => {
      const [fx, fy, fz = 0] = item.position;
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(fx, fz, -fy); // sim(x,y,z) → three(x,z,-y)
      scene.add(mesh);
      return mesh;
    });

    return () => {
      for (const m of meshes) scene.remove(m);
      geo.dispose();
      mat.dispose();
    };
  }, [config]);

  const connectBroadcast = useCallback(() => {
    wsRef.current?.close();
    // Fresh connection: discard particle state and wait for the server's keyframe.
    particleHasKeyframeRef.current = false;
    particleSlotToIdxRef.current.clear();
    particleIdxToSlotRef.current.length = 0;
    particleRawRef.current.clear();
    particleHashRef.current = 0;
    particleCrcFrameRef.current = 0;
    lastRkFrameRef.current = -PARTICLE_RK_COOLDOWN;
    if (particlesRef.current) particlesRef.current.count = 0;
    frozenHasKeyframeRef.current = false;
    frozenCidToIdxRef.current.clear();
    frozenIdxToCidRef.current.length = 0;
    frozenHashRef.current = 0;
    frozenCrcFrameRef.current = 0;
    lastFrozenRkFrameRef.current = -FROZEN_RK_COOLDOWN;
    if (frozenRef.current) frozenRef.current.count = 0;
    const ws = new WebSocket(broadcastUrl);
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;

    ws.onmessage = (evt) => {
      if (typeof evt.data === 'string') {
        try {
          const msg = JSON.parse(evt.data);
          if (msg.type === 'roster') {
            rosterRef.current = msg.organisms ?? [];
            console.log('[sim] roster', msg.organisms);
            rebuildEntityGroups();
          } else if (msg.type === 'body_schema') {
            bodySchemaRef.current = msg.schemas ?? {};
            const schemas = msg.schemas ?? {};
            for (const [bt, s] of Object.entries(schemas) as [string, any][]) {
              const meshCount = Object.keys(s.meshes ?? {}).length;
              const geomCounts = (s.bodies ?? []).map((b: any) => `${b.name}:${(b.geoms ?? []).length}geoms`);
              console.log(`[sim] body_schema ${bt}: ${meshCount} meshes, bodies=[${geomCounts.join(', ')}]`);
            }
            rebuildEntityGroups();
          } else if (msg.type === 'site_registry') {
            siteRegistryRef.current = msg.sites ?? [];
          } else if (msg.type === 'sun') {
            sunRef.current = {
              direction: msg.direction,
              rgb:       msg.rgb,
              intensity: msg.intensity,
              is_day:    msg.is_day,
            };
          } else if (msg.type === 'stats' && tickRateRef.current) {
            const hz = msg.actual_tick_rate as number;
            tickRateRef.current.textContent = hz > 0 ? hz.toFixed(1) : '—';
          }
        } catch { /* ignore malformed */ }
        return;
      }
      const buf = evt.data as ArrayBuffer;
      if (buf.byteLength < 18) return;
      latestBufRef.current = { buf, view: new DataView(buf) };
    };

    ws.onerror = () => setBroadcastOffline(true);
    ws.onclose = () => { wsRef.current = null; };
  }, [broadcastUrl, rebuildEntityGroups]);

  useEffect(() => {
    setBroadcastOffline(false);
    connectBroadcast();
    return () => { wsRef.current?.close(); wsRef.current = null; };
  }, [connectBroadcast]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    // ── Renderer ────────────────────────────────────────────────────────────
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true });
    } catch {
      setWebglUnavailable(true);
      return;
    }
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    renderer.domElement.style.touchAction = 'none';
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.3;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    mount.appendChild(renderer.domElement);

    // World extents (for ground plane + shadow frustum). Fall back to 128 if config absent.
    const dims = (config as any)?.universe?.grid_dimensions as number[] | undefined;
    const worldW = dims?.[0] ?? 128;
    const worldH = dims?.[1] ?? 128;
    const worldMax = Math.max(worldW, worldH);
    const cx = worldW / 2;
    const cz = -worldH / 2;  // sim y maps to three -z

    // ── Scene ───────────────────────────────────────────────────────────────
    const scene = new THREE.Scene();
    const SKY_DAY   = new THREE.Color(0x8fb8e8);
    const SKY_NIGHT = new THREE.Color(0x05070f);
    scene.background = SKY_NIGHT.clone();
    scene.fog = new THREE.FogExp2(0x05070f, 0.0035);
    sceneRef.current = scene;

    // ── Camera ──────────────────────────────────────────────────────────────
    // Coordinate mapping: sim(x, y, z) → Three.js(x, z, -y). FPS camera: it stands
    // on the ground plane at a fixed eye height and is driven by pointer-lock look +
    // WASD walk (below). Starts near one edge of the patch looking across it.
    const camera = new THREE.PerspectiveCamera(75, mount.clientWidth / mount.clientHeight, 0.1, worldMax * 20);

    // ── FPS controls: pointer-lock mouselook + WASD walk ─────────────────────
    // Tunables.
    const EYE_HEIGHT       = 2.0;    // camera height above the ground plane (three-y)
    const MOVE_SPEED       = 20;     // walk speed, world units / second
    const SPRINT_MULT      = 2.5;    // hold Shift to sprint
    const LOOK_SENSITIVITY = 0.002;  // radians of view rotation per pixel of mouse motion
    const PICK_PX          = 30;     // screen-space fallback pick radius for center selection

    camera.position.set(cx, EYE_HEIGHT, 8);  // three-z ≈ 8 is near the sim-y=0 edge
    camera.rotation.order = 'YXZ';
    let yaw   = 0;    // look along -z, into the patch
    let pitch = 0;    // level
    camera.rotation.set(pitch, yaw, 0, 'YXZ');

    let locked = false;
    const keys = new Set<string>();
    let prevHoveredFromCanvas: string | undefined;
    const canvas = renderer.domElement;

    // ── Lighting — sun-driven (day/night, shadows) ───────────────────────────
    // Ambient stands in for skylight; its intensity is scaled each frame by the
    // sun so nights go dark. Base color cool to read as sky bounce.
    const ambient = new THREE.AmbientLight(0x93a7c4, 0.9);
    scene.add(ambient);

    // The sun: a directional light positioned each frame from the sun message.
    // Casts shadows onto the ground plane below.
    const sunLight = new THREE.DirectionalLight(0xffffff, 2.4);
    sunLight.position.set(cx, worldMax, cz);
    sunLight.target.position.set(cx, 0, cz);
    sunLight.castShadow = true;
    sunLight.shadow.mapSize.set(2048, 2048);
    const shadowCam = sunLight.shadow.camera as THREE.OrthographicCamera;
    shadowCam.left = -worldMax; shadowCam.right = worldMax;
    shadowCam.top = worldMax;   shadowCam.bottom = -worldMax;
    shadowCam.near = 1;         shadowCam.far = worldMax * 4;
    sunLight.shadow.bias = -0.0008;
    scene.add(sunLight);
    scene.add(sunLight.target);

    // ── Visible sun body ─────────────────────────────────────────────────────
    // A glowing sphere drawn at the sun's world position. It is the light source,
    // so it neither casts nor receives shadows; it just marks where the sun is.
    // Built as a unit sphere and scaled each frame to a fixed fraction of its
    // distance, so it holds a constant angular size regardless of world size or
    // orbit radius (like a real sun).
    const SUN_ANGULAR = 0.045;   // orb radius as a fraction of its distance
    const sunOrbGeo = new THREE.SphereGeometry(1, 24, 16);
    const sunOrbMat = new THREE.MeshBasicMaterial({ color: 0xffffff, fog: false });
    const sunOrb = new THREE.Mesh(sunOrbGeo, sunOrbMat);
    sunOrb.frustumCulled = false;
    scene.add(sunOrb);
    // A soft halo so it reads as a glow rather than a hard ball.
    const sunHaloGeo = new THREE.SphereGeometry(1, 24, 16);
    const sunHaloMat = new THREE.MeshBasicMaterial({
      color: 0xffffff, transparent: true, opacity: 0.25, fog: false,
      blending: THREE.AdditiveBlending, depthWrite: false,
    });
    const sunHalo = new THREE.Mesh(sunHaloGeo, sunHaloMat);
    sunHalo.frustumCulled = false;
    scene.add(sunHalo);

    // ── Ground plane (receives shadows) ──────────────────────────────────────
    const groundGeo = new THREE.PlaneGeometry(worldMax * 3, worldMax * 3);
    const groundMat = new THREE.MeshStandardMaterial({
      color: 0x3a3226, roughness: 0.95, metalness: 0.0,
    });
    const ground = new THREE.Mesh(groundGeo, groundMat);
    ground.rotation.x = -Math.PI / 2;  // lie flat on the XZ plane at y=0 (sim z=0)
    ground.position.set(cx, 0, cz);
    ground.receiveShadow = true;
    scene.add(ground);

    // ── Floor grid ───────────────────────────────────────────────────────────
    const gridHelper = new THREE.GridHelper(512, 64, 0x2a2a2a, 0x1a1a1a);
    gridHelper.position.set(64, 0.02, -64);
    scene.add(gridHelper);

    // ── Sphere fallback InstancedMesh (for fixtures without body schema) ─────
    const fixGeo = new THREE.SphereGeometry(1, 8, 6);
    // NOTE: no `vertexColors: true`. Per-instance colour comes from instanceColor
    // (setColorAt), which the shader applies via USE_INSTANCING_COLOR on its own.
    // Enabling vertexColors here turns on USE_COLOR, which multiplies by the
    // geometry's per-vertex `color` attribute — absent on this sphere, so it reads
    // as (0,0,0) and zeroes every instance to black.
    const fixMat = new THREE.MeshStandardMaterial({
      color: new THREE.Color(1.0, 1.0, 1.0),
      roughness: 0.65,
      metalness: 0.05,
    });
    const fixtures = new THREE.InstancedMesh(fixGeo, fixMat, MAX_FIXTURES);
    fixtures.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    fixtures.frustumCulled = false;
    fixtures.castShadow = true;
    fixtures.count = 0;
    scene.add(fixtures);
    fixturesRef.current = fixtures;

    // ── Managed-field particles (plant cells) InstancedMesh ──────────────────
    const partGeo = new THREE.SphereGeometry(1, 6, 4);
    // See fixMat note: instanceColor alone colours these; vertexColors would zero them.
    const partMat = new THREE.MeshStandardMaterial({
      roughness: 0.8, metalness: 0.0,
    });
    const particles = new THREE.InstancedMesh(partGeo, partMat, MAX_PARTICLES);
    particles.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    particles.frustumCulled = false;
    particles.castShadow = true;
    particles.count = 0;
    scene.add(particles);
    particlesRef.current = particles;

    // ── Frozen (LOD unit) cells InstancedMesh ─────────────────────────────────
    // Demoted plant structure: static, so instance matrices are written on add and
    // never touched per frame. Sized well past the particle cap.
    const frozenGeo = new THREE.SphereGeometry(1, 6, 4);
    const frozenMat = new THREE.MeshStandardMaterial({ roughness: 0.85, metalness: 0.0 });
    const frozen = new THREE.InstancedMesh(frozenGeo, frozenMat, MAX_FROZEN);
    frozen.instanceMatrix.setUsage(THREE.StaticDrawUsage);   // rarely changes
    frozen.frustumCulled = false;
    frozen.castShadow = true;
    frozen.count = 0;
    scene.add(frozen);
    frozenRef.current = frozen;

    // ── Site points ──────────────────────────────────────────────────────────
    const sitePosArr = new Float32Array(MAX_SITES * 3);
    const siteColArr = new Float32Array(MAX_SITES * 3);
    const siteGeo = new THREE.BufferGeometry();
    siteGeo.setAttribute('position', new THREE.BufferAttribute(sitePosArr, 3));
    siteGeo.setAttribute('color',    new THREE.BufferAttribute(siteColArr, 3));
    siteGeo.setDrawRange(0, 0);
    scene.add(new THREE.Points(siteGeo, new THREE.PointsMaterial({
      size: 5, sizeAttenuation: false, vertexColors: true,
    })));

    // ── Input: raycasting + FPS control handlers ─────────────────────────────
    const raycaster = new THREE.Raycaster();
    let prevHighlightedId: string | undefined;

    // Movement scratch vectors + fixed screen-center pick point.
    const _right  = new THREE.Vector3();
    const _move   = new THREE.Vector3();
    const _center = new THREE.Vector2(0, 0);
    const clock   = new THREE.Clock();

    const findClosestEntityOnScreen = (px: number, py: number, rect: DOMRect, threshold: number): string | undefined => {
      let best: string | undefined;
      let bestDist = threshold;
      for (const [eid, pos] of entityPositionsRef.current) {
        _pickVec.copy(pos).project(camera);
        if (_pickVec.z > 1) continue; // behind camera
        const sx = (_pickVec.x + 1) / 2 * rect.width;
        const sy = (-_pickVec.y + 1) / 2 * rect.height;
        const d = Math.hypot(sx - px, sy - py);
        if (d < bestDist) { bestDist = d; best = eid; }
      }
      return best;
    };
    const _pickVec = new THREE.Vector3();

    // Raycast from the screen center (the crosshair) and return the entity under it,
    // if any. Same three-tier resolution as the old mouse pick: schema meshes, then
    // the sphere-fallback InstancedMesh, then a screen-space nearest fallback.
    const pickFromCenter = (): string | undefined => {
      raycaster.setFromCamera(_center, camera);
      const schemaTargets = raycasterTargetsRef.current;
      if (schemaTargets.length > 0) {
        const hits = raycaster.intersectObjects(schemaTargets, false);
        if (hits.length > 0) return (hits[0].object as THREE.Mesh).userData.entityId;
      }
      const fhits = raycaster.intersectObject(fixtures);
      const instanceId = fhits.length > 0 ? fhits[0].instanceId : undefined;
      const orgIdx = instanceId !== undefined ? (instanceIdToOrgIdxRef.current.get(instanceId) ?? -1) : -1;
      if (orgIdx !== -1) return rosterRef.current.find(r => r.idx === orgIdx)?.entity_id;
      const rect = canvas.getBoundingClientRect();
      return findClosestEntityOnScreen(rect.width / 2, rect.height / 2, rect, PICK_PX);
    };

    const selectFromCenter = () => {
      const id = pickFromCenter();
      onOrganismClickRef.current?.(
        id ? (id === selectedEntityIdRef.current ? undefined : id) : undefined
      );
    };

    // A click on the canvas engages pointer lock; once locked, a click selects
    // whatever is under the crosshair. The lock-engaging click fires while still
    // unlocked, so it never doubles as a selection.
    const onCanvasMouseDown = () => {
      if (!locked) { canvas.requestPointerLock(); return; }
      selectFromCenter();
    };

    const onPointerLockChange = () => {
      locked = document.pointerLockElement === canvas;
      setIsLocked(locked);
      if (!locked) keys.clear();  // drop held keys so movement stops cleanly on Esc
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!locked) return;
      yaw   -= e.movementX * LOOK_SENSITIVITY;
      pitch -= e.movementY * LOOK_SENSITIVITY;
      pitch = Math.max(-Math.PI / 2 + 0.01, Math.min(Math.PI / 2 - 0.01, pitch));
      camera.rotation.set(pitch, yaw, 0, 'YXZ');
    };

    const onKeyDown = (e: KeyboardEvent) => { keys.add(e.code); };
    const onKeyUp   = (e: KeyboardEvent) => { keys.delete(e.code); };

    canvas.addEventListener('mousedown', onCanvasMouseDown);
    document.addEventListener('pointerlockchange', onPointerLockChange);
    document.addEventListener('mousemove', onMouseMove);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup',   onKeyUp);

    // ── rAF loop ─────────────────────────────────────────────────────────────
    let animId: number;

    function animate() {
      animId = requestAnimationFrame(animate);
      const dt = clock.getDelta();

      // Update fixtures from latest broadcast frame
      const frame = latestBufRef.current;
      if (frame) {
        const { buf, view } = frame;
        const tick = view.getBigUint64(0, true);
        if (tickDisplayRef.current) tickDisplayRef.current.textContent = tick.toString();
        const fixture_count  = view.getUint16(12, true);
        const site_count     = view.getUint16(14, true);
        const particleUpdCount = view.getUint16(16, true);  // UPDATED records this frame, not a total
        let off = 18;
        let n = 0;  // sphere fallback instance counter
        instanceIdToOrgIdxRef.current.clear();

        const slotToEntity = slotToEntityRef.current;
        const entityGroups = entityGroupsRef.current;
        const seenPositions = new Set<string>();

        for (let i = 0; i < fixture_count; i++) {
          if (off + RECORD_BYTES > buf.byteLength) break;
          const slotIdx = view.getUint16(off,      true);
          const orgIdx  = view.getUint16(off + 2,  true);
          const x  = view.getFloat32(off + 4,  true);
          const y  = view.getFloat32(off + 8,  true);
          const z  = view.getFloat32(off + 12, true);
          const r  = view.getFloat32(off + 16, true);
          const qw        = view.getFloat32(off + 23, true);
          const qx        = view.getFloat32(off + 27, true);
          const qy        = view.getFloat32(off + 31, true);
          const qz        = view.getFloat32(off + 35, true);
          const geomScale = view.getFloat32(off + 39, true);
          off += RECORD_BYTES;

          // Track world position for each entity (first slot per entity wins)
          const eidForSlot = slotToEntity.get(slotIdx)?.entityId
            ?? rosterRef.current.find(r => r.idx === orgIdx)?.entity_id;
          if (eidForSlot && !seenPositions.has(eidForSlot)) {
            seenPositions.add(eidForSlot);
            let ep = entityPositionsRef.current.get(eidForSlot);
            if (!ep) { ep = new THREE.Vector3(); entityPositionsRef.current.set(eidForSlot, ep); }
            ep.set(x, z, -y);
          }

          // If this slot has a schema-rendered group, update its transform.
          const slotInfo = slotToEntity.get(slotIdx);
          if (slotInfo) {
            const groups = entityGroups.get(slotInfo.entityId);
            const group  = groups?.[slotInfo.segIdx];
            if (group) {
              if (slotInfo.segIdx === 0) {
                const q = simQuatToThree(qw, qx, qy, qz);
                console.log(`[sim] seg0 sim=(${x.toFixed(3)},${y.toFixed(3)},${z.toFixed(3)}) three_pos=(${x.toFixed(3)},${z.toFixed(3)},${(-y).toFixed(3)}) three_quat=(${q.x.toFixed(3)},${q.y.toFixed(3)},${q.z.toFixed(3)},${q.w.toFixed(3)})`);
              }
              group.position.set(x, z, -y);
              group.quaternion.copy(simQuatToThree(qw, qx, qy, qz));
              group.scale.setScalar(geomScale > 0 ? geomScale : 1);
            }
            continue; // don't also render a fallback sphere
          }

          // Fallback: render as a sphere instance — skip unowned/dead slots
          if (orgIdx === 0xFFFF) continue;
          const orgEntry = rosterRef.current.find(r => r.idx === orgIdx);
          if (!orgEntry || orgEntry.state === 'dead') continue;
          if (n >= MAX_FIXTURES) continue;
          _dummy.position.set(x, z, -y);
          _dummy.scale.setScalar(Math.max(0.5, r));
          _dummy.updateMatrix();
          fixtures.setMatrixAt(n, _dummy.matrix);
          fixtures.setColorAt(n, _color.setRGB(0.90, 0.48, 0.15));
          instanceIdToOrgIdxRef.current.set(n, orgIdx);
          n++;
        }

        fixtures.count = n;
        fixtures.instanceMatrix.needsUpdate = true;
        if (fixtures.instanceColor) fixtures.instanceColor.needsUpdate = true;

        // Parse site records from binary (follow all fixture records)
        const registry = siteRegistryRef.current;
        let si = 0;
        for (let i = 0; i < site_count; i++) {
          if (off + 14 > buf.byteLength) break;
          const siteId = view.getUint16(off,      true);
          const sx     = view.getFloat32(off + 2,  true);
          const sy     = view.getFloat32(off + 6,  true);
          const sz     = view.getFloat32(off + 10, true);
          off += 14;
          if (si >= MAX_SITES) continue;
          sitePosArr[si * 3]     = sx;
          sitePosArr[si * 3 + 1] = sz;
          sitePosArr[si * 3 + 2] = -sy;
          const name = registry[siteId] ?? '';
          const isMouth = name.endsWith('_mouth');
          siteColArr[si * 3]     = isMouth ? 0.98 : 0.38;
          siteColArr[si * 3 + 1] = isMouth ? 0.52 : 0.80;
          siteColArr[si * 3 + 2] = isMouth ? 0.18 : 0.98;
          si++;
        }
        siteGeo.setDrawRange(0, si);
        if (si > 0) {
          (siteGeo.attributes.position as THREE.BufferAttribute).needsUpdate = true;
          (siteGeo.attributes.color    as THREE.BufferAttribute).needsUpdate = true;
        }

        // Particle section (managed-field cells, e.g. plants): 7-byte sub-header
        // [flags u8][removed_count u16][state_hash u32], then particleUpdCount 25-byte
        // updated records, then removed_count u16 slots. Keyframe (flags bit0) = full
        // set (client clears first); otherwise only changed/new cells, and unmentioned
        // cells persist. state_hash is a running XOR of crc32 over each active record;
        // we maintain the same hash incrementally and, on a mismatch, ask for a
        // keyframe — so sync is verified on the data with O(changed) work per frame.
        const pmesh = particlesRef.current;
        if (pmesh && off + PARTICLE_SUBHEADER_BYTES <= buf.byteLength) {
          const pflags = view.getUint8(off);
          const removedCount = view.getUint16(off + 1, true);
          const frameHash = view.getUint32(off + 3, true);
          off += PARTICLE_SUBHEADER_BYTES;
          const slotToIdx = particleSlotToIdxRef.current;
          const idxToSlot = particleIdxToSlotRef.current;
          const rawShadow = particleRawRef.current;
          const isKeyframe = (pflags & 0x01) !== 0;
          let pChanged = false;

          // A delta with no keyframe yet has no baseline to apply against; skip it (the
          // server forces a keyframe on connect). Particles are the last section, so
          // skipping needs no offset fixup.
          if (isKeyframe || particleHasKeyframeRef.current) {
            if (isKeyframe) {           // keyframe: drop existing set, rebuild from records
              slotToIdx.clear();
              idxToSlot.length = 0;
              rawShadow.clear();
              particleHashRef.current = 0;   // records below re-accumulate it
              pmesh.count = 0;
              particleHasKeyframeRef.current = true;
              pChanged = true;
            }

            for (let i = 0; i < particleUpdCount; i++) {
              if (off + PARTICLE_RECORD_BYTES > buf.byteLength) break;
              const recStart = off;
              const slot = view.getUint16(off, true);
              const px = view.getFloat32(off + 6,  true);
              const py = view.getFloat32(off + 10, true);
              const pz = view.getFloat32(off + 14, true);
              const pr = view.getFloat32(off + 18, true);
              const cr = view.getUint8(off + 22);
              const cg = view.getUint8(off + 23);
              const cb = view.getUint8(off + 24);
              off += PARTICLE_RECORD_BYTES;
              // Maintain the running hash: xor out the old record (if any), xor in new.
              const raw = new Uint8Array(buf.slice(recStart, recStart + PARTICLE_RECORD_BYTES));
              const prev = rawShadow.get(slot);
              if (prev !== undefined) particleHashRef.current = (particleHashRef.current ^ crc32(prev)) >>> 0;
              particleHashRef.current = (particleHashRef.current ^ crc32(raw)) >>> 0;
              rawShadow.set(slot, raw);
              let idx = slotToIdx.get(slot);
              if (idx === undefined) {   // new cell: append at the end
                idx = pmesh.count;
                if (idx >= MAX_PARTICLES) continue;
                slotToIdx.set(slot, idx);
                idxToSlot[idx] = slot;
                pmesh.count = idx + 1;
              }
              _dummy.position.set(px, pz, -py);  // sim(x,y,z) -> three(x,z,-y)
              _dummy.scale.setScalar(Math.max(0.03, pr));
              _dummy.updateMatrix();
              pmesh.setMatrixAt(idx, _dummy.matrix);
              pmesh.setColorAt(idx, _color.setRGB(cr / 255, cg / 255, cb / 255));
              pChanged = true;
            }

            for (let i = 0; i < removedCount; i++) {
              if (off + 2 > buf.byteLength) break;
              const slot = view.getUint16(off, true);
              off += 2;
              const prev = rawShadow.get(slot);
              if (prev !== undefined) particleHashRef.current = (particleHashRef.current ^ crc32(prev)) >>> 0;
              rawShadow.delete(slot);
              const idx = slotToIdx.get(slot);
              if (idx === undefined) continue;
              const last = pmesh.count - 1;
              if (idx !== last) {        // move last instance into the freed slot (keep dense)
                const lastSlot = idxToSlot[last];
                pmesh.getMatrixAt(last, _dummy.matrix);
                pmesh.setMatrixAt(idx, _dummy.matrix);
                if (pmesh.instanceColor) { pmesh.getColorAt(last, _color); pmesh.setColorAt(idx, _color); }
                slotToIdx.set(lastSlot, idx);
                idxToSlot[idx] = lastSlot;
              }
              slotToIdx.delete(slot);
              idxToSlot.length = last;
              pmesh.count = last;
              pChanged = true;
            }

            if (pChanged) {
              pmesh.instanceMatrix.needsUpdate = true;
              if (pmesh.instanceColor) pmesh.instanceColor.needsUpdate = true;
            }

            // O(1) data audit: our running hash vs the server's. On divergence — a
            // dropped delta left us stale — request a keyframe; the cooldown avoids a
            // storm and retries if that keyframe is itself lost.
            particleCrcFrameRef.current++;
            if (particleHashRef.current !== frameHash &&
                particleCrcFrameRef.current - lastRkFrameRef.current >= PARTICLE_RK_COOLDOWN) {
              try { wsRef.current?.send("rk"); } catch { /* socket not ready */ }
              lastRkFrameRef.current = particleCrcFrameRef.current;
            }
          } else {
            // Connected but no keyframe yet (e.g. we missed the on-connect one in a
            // race): deltas are unusable without a baseline, so actively ask for a
            // keyframe (throttled) instead of waiting for the periodic backstop.
            particleCrcFrameRef.current++;
            if (particleCrcFrameRef.current - lastRkFrameRef.current >= PARTICLE_RK_COOLDOWN) {
              try { wsRef.current?.send("rk"); } catch { /* socket not ready */ }
              lastRkFrameRef.current = particleCrcFrameRef.current;
            }
          }
        }

        // Frozen section (LOD unit cells): 13-byte sub-header [flags u8][added u32]
        // [removed u32][cid_set_hash u32], then `added` 27-byte records (cid u32, owner
        // u32, xyz f32, radius f32, rgb u8), then `removed` u32 cids. Keyframe (flags
        // bit0) = full active set (clear first). Frozen cells are static: matrices are
        // written on add and never touched again, so this costs nothing on unchanged
        // frames. cid_set_hash is a running XOR of crc32(cid) over the active set; we
        // keep the same hash and, on a mismatch (a dropped frozen delta left us short a
        // cell — the "gaps in growing trees" case), ask for a frozen keyframe ("rfk")
        // rather than waiting for the periodic backstop.
        const fmesh = frozenRef.current;
        if (fmesh && off + FROZEN_SUBHEADER_BYTES <= buf.byteLength) {
          const fflags = view.getUint8(off);
          const addedCount = view.getUint32(off + 1, true);
          const fRemovedCount = view.getUint32(off + 5, true);
          const fFrameHash = view.getUint32(off + 9, true);
          off += FROZEN_SUBHEADER_BYTES;
          const isFKeyframe = (fflags & 0x01) !== 0;
          if (isFKeyframe || frozenHasKeyframeRef.current) {
            const cidToIdx = frozenCidToIdxRef.current;
            const idxToCid = frozenIdxToCidRef.current;
            let fChanged = false;
            if (isFKeyframe) {
              cidToIdx.clear();
              idxToCid.length = 0;
              fmesh.count = 0;
              frozenHashRef.current = 0;   // records below re-accumulate it
              frozenHasKeyframeRef.current = true;
              fChanged = true;
            }
            for (let i = 0; i < addedCount; i++) {
              if (off + FROZEN_RECORD_BYTES > buf.byteLength) break;
              const cid = view.getUint32(off, true);
              const fx = view.getFloat32(off + 8,  true);
              const fy = view.getFloat32(off + 12, true);
              const fz = view.getFloat32(off + 16, true);
              const fr = view.getFloat32(off + 20, true);
              const cr = view.getUint8(off + 24);
              const cg = view.getUint8(off + 25);
              const cb = view.getUint8(off + 26);
              off += FROZEN_RECORD_BYTES;
              let idx = cidToIdx.get(cid);
              if (idx === undefined) {
                idx = fmesh.count;
                if (idx >= MAX_FROZEN) continue;
                cidToIdx.set(cid, idx);
                idxToCid[idx] = cid;
                fmesh.count = idx + 1;
                // xor in only on genuine insert, so a re-sent cid does not double-count.
                frozenHashRef.current = (frozenHashRef.current ^ crc32Cid(cid)) >>> 0;
              }
              _dummy.position.set(fx, fz, -fy);  // sim(x,y,z) -> three(x,z,-y)
              _dummy.scale.setScalar(Math.max(0.03, fr));
              _dummy.updateMatrix();
              fmesh.setMatrixAt(idx, _dummy.matrix);
              fmesh.setColorAt(idx, _color.setRGB(cr / 255, cg / 255, cb / 255));
              fChanged = true;
            }
            for (let i = 0; i < fRemovedCount; i++) {
              if (off + 4 > buf.byteLength) break;
              const cid = view.getUint32(off, true);
              off += 4;
              const idx = cidToIdx.get(cid);
              if (idx === undefined) continue;
              // xor out only when we actually held it, keeping the hash in step.
              frozenHashRef.current = (frozenHashRef.current ^ crc32Cid(cid)) >>> 0;
              const last = fmesh.count - 1;
              if (idx !== last) {
                const lastCid = idxToCid[last];
                fmesh.getMatrixAt(last, _dummy.matrix);
                fmesh.setMatrixAt(idx, _dummy.matrix);
                if (fmesh.instanceColor) { fmesh.getColorAt(last, _color); fmesh.setColorAt(idx, _color); }
                cidToIdx.set(lastCid, idx);
                idxToCid[idx] = lastCid;
              }
              cidToIdx.delete(cid);
              idxToCid.length = last;
              fmesh.count = last;
              fChanged = true;
            }
            if (fChanged) {
              fmesh.instanceMatrix.needsUpdate = true;
              if (fmesh.instanceColor) fmesh.instanceColor.needsUpdate = true;
            }

            // O(1) data audit: our frozen cid-set hash vs the server's. On divergence —
            // a dropped frozen delta left the tree with gaps — request a keyframe; the
            // cooldown avoids a storm and retries if that keyframe is itself lost.
            frozenCrcFrameRef.current++;
            if (frozenHashRef.current !== fFrameHash &&
                frozenCrcFrameRef.current - lastFrozenRkFrameRef.current >= FROZEN_RK_COOLDOWN) {
              try { wsRef.current?.send("rfk"); } catch { /* socket not ready */ }
              lastFrozenRkFrameRef.current = frozenCrcFrameRef.current;
            }
          } else {
            // Connected but no frozen keyframe yet (missed the on-connect one in a race):
            // appends are unusable without a baseline, so actively ask for a keyframe
            // (throttled). Frozen is the last section, so leaving `off` mid-section is fine.
            frozenCrcFrameRef.current++;
            if (frozenCrcFrameRef.current - lastFrozenRkFrameRef.current >= FROZEN_RK_COOLDOWN) {
              try { wsRef.current?.send("rfk"); } catch { /* socket not ready */ }
              lastFrozenRkFrameRef.current = frozenCrcFrameRef.current;
            }
          }
        }
      }

      // Hover highlight: swap material color (MeshBasicMaterial has no emissive)
      const NEON_BASE    = new THREE.Color(0.90, 0.48, 0.15);
      const NEON_HOVER   = new THREE.Color(1.0, 0.82, 0.40);
      const currentHighlight = hoveredEntityIdRef.current;
      if (currentHighlight !== prevHighlightedId) {
        if (prevHighlightedId !== undefined) {
          const prevGroups = entityGroupsRef.current.get(prevHighlightedId);
          if (prevGroups) {
            for (const g of prevGroups) {
              g.traverse(obj => {
                if (obj instanceof THREE.Mesh) (obj.material as THREE.MeshStandardMaterial).color.copy(NEON_BASE);
              });
            }
          }
        }
        if (currentHighlight !== undefined) {
          const newGroups = entityGroupsRef.current.get(currentHighlight);
          if (newGroups) {
            for (const g of newGroups) {
              g.traverse(obj => {
                if (obj instanceof THREE.Mesh) (obj.material as THREE.MeshStandardMaterial).color.copy(NEON_HOVER);
              });
            }
          }
        }
        prevHighlightedId = currentHighlight;
      }

      // Hover from the crosshair while locked: report whatever the center ray hits.
      if (locked) {
        const hid = pickFromCenter();
        if (hid !== prevHoveredFromCanvas) {
          prevHoveredFromCanvas = hid;
          onOrganismHoverRef.current?.(hid);
        }
      }

      // ── Camera: follow-cam when snapped, else FPS walk at fixed eye height ────
      const snapId = snapToEntityIdRef.current;
      if (snapId) {
        // Follow-cam: keep camera behind and above the snapped entity.
        const targetPos = entityPositionsRef.current.get(snapId);
        if (targetPos) {
          camera.getWorldDirection(_fwd);
          _fwd.y = 0;
          if (_fwd.lengthSq() > 0.001) _fwd.normalize(); else _fwd.set(0, 0, -1);
          camera.position.set(
            targetPos.x - _fwd.x * 12,
            targetPos.y + 6,
            targetPos.z - _fwd.z * 12,
          );
        }
      } else {
        // FPS walk: translate along the flattened look direction, pinned to eye height.
        if (locked) {
          const sprint = keys.has('ShiftLeft') || keys.has('ShiftRight') ? SPRINT_MULT : 1;
          const step = MOVE_SPEED * sprint * dt;
          camera.getWorldDirection(_fwd);
          _fwd.y = 0;
          if (_fwd.lengthSq() > 1e-6) _fwd.normalize();
          _right.crossVectors(_fwd, camera.up).normalize();  // right of the look direction
          _move.set(0, 0, 0);
          if (keys.has('KeyW')) _move.add(_fwd);
          if (keys.has('KeyS')) _move.sub(_fwd);
          if (keys.has('KeyD')) _move.add(_right);
          if (keys.has('KeyA')) _move.sub(_right);
          if (_move.lengthSq() > 1e-6) {
            _move.normalize().multiplyScalar(step);
            camera.position.x += _move.x;
            camera.position.z += _move.z;
          }
        }
        camera.position.y = EYE_HEIGHT;  // pin eye height every frame
      }

      // ── Sun: drive the directional light, ambient, and sky from sun state ────
      const sun = sunRef.current;
      if (sun) {
        // The world server publishes the sun's LOCAL direction (this world's frame),
        // not a world-space position. Sim direction (dx, dy, dz), dz = up, maps to
        // three (dx, dz, -dy). We hang the sun in the sky along that direction at a
        // fixed render distance; it is a celestial backdrop, not a world-space object.
        const [dx, dy, dz] = sun.direction;
        const SKY_DIST = worldMax * 4;              // how far the sun sits in the sky (render-only)
        const tx = cx + dx * SKY_DIST;
        const ty =      dz * SKY_DIST;              // three-y (height) from sim-z
        const tz = cz - dy * SKY_DIST;              // three-z from sim-y
        sunLight.position.set(tx, ty, tz);
        sunLight.target.position.set(cx, 0, cz);
        sunLight.color.setRGB(sun.rgb[0], sun.rgb[1], sun.rgb[2]);
        sunLight.intensity = 2.4 * sun.intensity;
        // Ambient (skylight) fades with the sun but keeps a small night floor.
        ambient.intensity = 0.12 + 0.8 * sun.intensity;
        // Sky lerps night→day by intensity; warm slightly near the horizon.
        _skyColor.copy(SKY_NIGHT).lerp(SKY_DAY, Math.min(1, sun.intensity));
        (scene.background as THREE.Color).copy(_skyColor);
        scene.fog!.color.copy(_skyColor);

        // Visible sun body along the sun direction, tinted by RGB. Hidden whenever the
        // sun is at or below the local horizon (three-y ≤ 0), so it never shows under
        // the floor. Scaled to a fixed angular size so it reads the same at any world size.
        const dist = Math.hypot(tx - cx, ty, tz - cz);
        const orbR = Math.max(1, dist * SUN_ANGULAR);
        sunOrb.position.set(tx, ty, tz);
        sunHalo.position.set(tx, ty, tz);
        sunOrb.scale.setScalar(orbR);
        sunHalo.scale.setScalar(orbR * 1.7);
        _color.setRGB(sun.rgb[0], sun.rgb[1], sun.rgb[2]);
        (sunOrb.material as THREE.MeshBasicMaterial).color.copy(_color);
        (sunHalo.material as THREE.MeshBasicMaterial).color.copy(_color);
        const vis = sun.is_day && ty > 0;
        sunOrb.visible = vis;
        sunHalo.visible = vis;
        (sunHalo.material as THREE.MeshBasicMaterial).opacity = 0.25 * Math.min(1, sun.intensity);
      }

      renderer.render(scene, camera);
    }
    animate();

    // ── WebGL context loss ───────────────────────────────────────────────────
    const handleContextLost = (e: Event) => {
      e.preventDefault(); // required to allow restoration
      cancelAnimationFrame(animId);
    };
    const handleContextRestored = () => {
      renderer.resetState();
      animate();
    };
    renderer.domElement.addEventListener('webglcontextlost',     handleContextLost);
    renderer.domElement.addEventListener('webglcontextrestored', handleContextRestored);

    // ── Resize ───────────────────────────────────────────────────────────────
    const resizeObs = new ResizeObserver(() => {
      camera.aspect = mount.clientWidth / mount.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(mount.clientWidth, mount.clientHeight);
    });
    resizeObs.observe(mount);

    return () => {
      cancelAnimationFrame(animId);
      resizeObs.disconnect();
      if (document.pointerLockElement === canvas) document.exitPointerLock();
      canvas.removeEventListener('mousedown', onCanvasMouseDown);
      document.removeEventListener('pointerlockchange', onPointerLockChange);
      document.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup',   onKeyUp);
      renderer.domElement.removeEventListener('webglcontextlost',     handleContextLost);
      renderer.domElement.removeEventListener('webglcontextrestored', handleContextRestored);
      // Clean up all entity groups
      for (const groups of entityGroupsRef.current.values()) {
        for (const g of groups) {
          g.traverse(obj => {
            if (obj instanceof THREE.Mesh) {
              obj.geometry.dispose();
              (obj.material as THREE.Material).dispose();
            }
          });
        }
      }
      entityGroupsRef.current.clear();
      slotToEntityRef.current.clear();
      sceneRef.current = null;
      fixturesRef.current = null;
      particlesRef.current = null;
      frozenRef.current = null;
      fixGeo.dispose();
      fixMat.dispose();
      partGeo.dispose();
      partMat.dispose();
      frozenGeo.dispose();
      frozenMat.dispose();
      siteGeo.dispose();
      groundGeo.dispose();
      groundMat.dispose();
      sunOrbGeo.dispose();
      sunOrbMat.dispose();
      sunHaloGeo.dispose();
      sunHaloMat.dispose();
      renderer.dispose();
      if (mount.contains(renderer.domElement)) mount.removeChild(renderer.domElement);
    };
  }, []);

  if (webglUnavailable) {
    return (
      <div className="broadcast-offline">
        <p>WEBGL UNAVAILABLE</p>
        <p className="broadcast-offline-hint">Your browser or GPU does not support WebGL</p>
      </div>
    );
  }

  if (broadcastOffline) {
    return (
      <div className="broadcast-offline">
        <p>SIM OFFLINE</p>
        <p className="broadcast-offline-hint">The simulation tunnel is currently down</p>
        <button onClick={() => { setBroadcastOffline(false); connectBroadcast(); }}>RETRY</button>
      </div>
    );
  }

  return (
    <div ref={mountRef} style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}>
      {/* Crosshair (only while locked): marks the center pick point for look/select. */}
      {isLocked && (
        <div style={{
          position: 'absolute', top: '50%', left: '50%', width: 6, height: 6,
          transform: 'translate(-50%, -50%)', borderRadius: '50%',
          border: '1px solid rgba(150,210,255,0.8)', pointerEvents: 'none',
        }} />
      )}
      {/* Control hint (only while unlocked). */}
      {!isLocked && (
        <div style={{
          position: 'absolute', bottom: 10, left: 10, pointerEvents: 'none',
          fontFamily: '"Space Mono", monospace', fontSize: 11,
          color: 'rgba(150,210,255,0.55)', letterSpacing: '0.06em',
        }}>
          CLICK TO LOOK · WASD MOVE · SHIFT SPRINT · ESC RELEASE
        </div>
      )}
      <div style={{
        position: 'absolute', top: 8, right: 10, pointerEvents: 'none',
        fontFamily: '"Space Mono", monospace', fontSize: 11,
        color: 'rgba(150,210,255,0.6)', letterSpacing: '0.08em',
      }}>
        TICK <span ref={tickDisplayRef}>—</span>
        <span style={{ marginLeft: 10, opacity: 0.7 }}>TPS <span ref={tickRateRef} style={{ display: 'inline-block', width: '4.5ch', textAlign: 'right' }}>—</span></span>
      </div>
    </div>
  );
}
